import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { concat, of, Subject } from 'rxjs';
import {
  catchError, debounceTime, distinctUntilChanged,
  filter, finalize, startWith, switchMap, takeUntil, tap
} from 'rxjs/operators';
import { DEFAULT_EDITOR_TOOLBAR_CONFIG, ToastService } from 'src/app/core';
import { ActivityStatus, GovernanceRequestApi } from 'src/app/core/api.gen';
import { ActiveDirectoryService } from 'src/app/core/active-directory.service';
import { DocumentUploadService } from 'src/app/core/document-upload.service';
import { LoaderService } from 'src/app/core/loader.service';
import { Logger } from 'src/app/utils/Logger';

@Component({
  selector: 'eos-governance-email',
  templateUrl: './governance-email.component.html'
})
export class GovernanceEmailComponent implements OnInit, OnDestroy {

  @Input() public activity: any;
  @Input() public governanceRequestId: number;
  onSubmit: Function;

  results: any = { attachments: [], customAttachments: [], activityTemplates: [] };
  tos: string[] = [];
  ccs: string[] = [];

  onDestroy$ = new Subject<void>();
  tosearchInput$ = new Subject<string>();
  ccsearchInput$ = new Subject<string>();
  toAdUsers: any = [];
  ccAdUsers: any = [];
  toShowLoader;
  ccShowLoader;

  uploadedFiles: any[] = [];
  existingDocs: any[] = [];
  selectedExistingDocs: any[] = [];

  selectedDocument: File;
  fileName: string;
  disableUpload = false;
  isDisabled = false;

  toolbarConfig = { ...DEFAULT_EDITOR_TOOLBAR_CONFIG };
 emailpattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;

 
  readonly allowedMineTypes = [
    'application/pdf', 'image/jpeg', 'image/png', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];
  readonly MAX_FILE_SIZE = 10 * 1024 * 1024;
  readonly MIN_FILE_SIZE = 1 * 1024;

  @ViewChild('fileControl', { static: true }) fileEle: ElementRef;

  constructor(
    public modal: NgbActiveModal,
    private documentUploadService: DocumentUploadService,
    private governanceApi: GovernanceRequestApi,
    private toast: ToastService,
    private loader: LoaderService,
    private ad: ActiveDirectoryService
  ) { }

  ngOnInit(): void {
    this.loadMessage(0);
    this.loadExistingDocs();
    this.toAdUsers = this.getAdUsers(true, this.tosearchInput$);
    this.ccAdUsers = this.getAdUsers(false, this.ccsearchInput$);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getAdUsers(setToLoader: boolean, subject: Subject<string>) {
    return concat(
      of([]),
      subject.pipe(
        takeUntil(this.onDestroy$),
        startWith(''),
        filter((v: string) => v !== null && v.length > 2),
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => this.setAdloader(setToLoader)),
        switchMap((term: string) => this.ad.getAdUsers(term).pipe(
          catchError(() => of([])),
          tap(() => {
            this.toShowLoader = false;
            this.ccShowLoader = false;
          })
        ))
      ));
  }

  setAdloader(isToLoader: boolean): void {
    if (isToLoader) {
      this.toShowLoader = true;
      this.ccShowLoader = false;
    } else {
      this.toShowLoader = false;
      this.ccShowLoader = true;
    }
  }

  private loadMessage(activityTemplateId: number): void {
    this.loader.show();
    this.documentUploadService
      .getGovernanceActivityEmailMessage(this.governanceRequestId, this.activity.id, activityTemplateId)
      .pipe(finalize(() => this.loader.clear()))
      .subscribe(resp => {
        this.results = resp || { attachments: [], customAttachments: [], activityTemplates: [] };
        this.results.attachments = this.results.attachments || [];
        this.results.activityTemplates = this.results.activityTemplates || [];
        this.tos = this.addAddresses(this.results.toAddresses);
        this.ccs = this.addAddresses(this.results.cc);
        this.loadAttachments();
      }, err => {
        this.toast.error('Unable to load the email template for this activity.');
        Logger.error(err);
      });
  }

  private loadAttachments(): void {
    this.documentUploadService
      .getGovernanceEmailAttachments(this.governanceRequestId, this.activity.activityId, this.results.activityTemplateId)
      .subscribe(resp => this.uploadedFiles = resp || []);
  }

  private loadExistingDocs(): void {
    this.governanceApi.getUploadedDocuments(this.governanceRequestId)
      .subscribe(resp => this.existingDocs = resp || []);
  }

  TemplateLoad(e): void {
    this.results.activityTemplateId = +e.target.value;
    this.loadMessage(this.results.activityTemplateId);
  }

  addAddresses(email: string): string[] {
    const emailArray: string[] = [];
    if (email && email.length > 0) {
      email.split(';').forEach(item => {
        if (item && item.trim() !== '') {
          emailArray.push(item.trim());
        }
      });
    }
    return emailArray;
  }

  get fileBrowserAllowedTypes(): string {
    return this.allowedMineTypes.join(',');
  }

  get selectedFileName(): string {
    return this.selectedDocument ? this.selectedDocument.name : '';
  }

  browseFile(): void {
    this.fileEle.nativeElement.click();
  }

  fileSelected(files: FileList): void {
    if (!files.item(0)) {
      return;
    }
    const file = files.item(0);
    if (file.size < this.MIN_FILE_SIZE || file.size > this.MAX_FILE_SIZE) {
      this.toast.error(`Invalid file size. File size should be between ${(this.MIN_FILE_SIZE / 1024).toFixed()} KB and ${(this.MAX_FILE_SIZE / 1024 / 1024).toFixed()} MB`);
      this.fileEle.nativeElement.value = null;
      return;
    }
    if (this.allowedMineTypes.indexOf(file.type) < 0) {
      this.toast.error('Invalid file type. Please select a valid file');
      this.fileEle.nativeElement.value = null;
      this.selectedDocument = null;
      return;
    }
    this.selectedDocument = file;
    this.fileName = file.name;
  }

  uploadDoc(): void {
    if (!this.selectedDocument) {
      this.toast.warning('Please select a file!');
      return;
    }
    if (this.uploadedFiles.some(x => x.fileName === this.selectedDocument.name)) {
      this.toast.error('Attachment name should be unique');
      return;
    }

    this.disableUpload = true;
    const details = {
      id: -1,
      fileName: this.selectedDocument.name,
      fileGuiId: '',
      governanceRequestActivityId: this.activity.id,
      emailTemplateId: this.results.activityTemplateId
    };

    this.documentUploadService.postGovernanceEmailAttachment(details, this.selectedDocument)
      .pipe(finalize(() => {
        this.disableUpload = false;
        this.selectedDocument = null;
        this.fileName = null;
        this.fileEle.nativeElement.value = null;
      }))
      .subscribe(() => {
        this.toast.success('File uploaded successfully!');
        this.loadAttachments();
      }, () => this.toast.error('Something went wrong while uploading the file.'));
  }

  attachSelectedExistingDocs(): void {
    if (!this.selectedExistingDocs || this.selectedExistingDocs.length === 0) {
      return;
    }
    if (this.selectedExistingDocs.some(d => this.uploadedFiles.some(u => u.fileGuiId === d.fileGuiId))) {
      this.toast.error('One or more selected documents are already attached.');
      return;
    }

    const attachments = this.selectedExistingDocs.map(d => ({
      id: -1,
      fileName: d.fileName,
      fileGuiId: d.fileGuiId,
      governanceRequestActivityId: this.activity.id,
      emailTemplateId: this.results.activityTemplateId
    }));

    this.documentUploadService.attachExistingGovernanceDocsToMail(attachments)
      .subscribe(() => {
        this.toast.success('Document(s) attached successfully!');
        this.selectedExistingDocs = [];
        this.loadAttachments();
      }, () => this.toast.error('Unable to attach the selected document(s).'));
  }

  deleteAttachment(doc: any): void {
    this.documentUploadService.deleteGovernanceEmailAttachment(doc)
      .subscribe(() => {
        this.toast.success('Attachment deleted!');
        this.loadAttachments();
      }, () => this.toast.error('Unable to delete the attachment.'));
  }

  removeTemplateAttachment(index: number): void {
    this.results.attachments.splice(index, 1);
  }

  downloadAttachment(doc: any): void {
    this.documentUploadService.downloadGovernanceFile({
      fileGuiId: doc.fileGuiId, fileName: doc.fileName, documentName: doc.fileName
    }).subscribe(resp => {
      const url = window.URL.createObjectURL(resp);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.setAttribute('style', 'display: none');
      a.href = url;
      a.download = doc.fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    }, err => Logger.error(err));
  }

  private patternValid(addresses: string[]): boolean {
    return (addresses || []).every(a => this.emailpattern.test(a));
  }

  sendMail(f: NgForm): void {
    if (f.invalid) {
      return;
    }
    if (!this.tos || this.tos.length === 0) {
      this.toast.warning('To address is required.');
      return;
    }
    if (!this.patternValid(this.tos) || !this.patternValid(this.ccs)) {
      this.toast.error('Please enter valid email addresses.');
      return;
    }

    this.results.toAddresses = this.tos.join(';');
    this.results.cc = this.ccs.join(';');
    this.results.governanceRequestId = this.governanceRequestId;
    this.results.governanceRequestActivityId = this.activity.id;
    this.results.customAttachments = this.uploadedFiles;

    this.isDisabled = true;
    this.loader.show();
    this.documentUploadService.sendGovernanceMail(this.governanceRequestId, this.results)
      .pipe(finalize(() => this.isDisabled = false))
      .subscribe(() => {
        this.governanceApi.updateGovernanceActivity(this.governanceRequestId, this.activity.id, ActivityStatus.Done)
          .pipe(finalize(() => this.loader.clear()))
          .subscribe(res => {
            this.toast.success('Email sent and activity completed.');
            if (this.onSubmit) {
              this.onSubmit(res.completedDate, res);
            }
            this.modal.close();
          });
      }, err => {
        this.loader.clear();
        this.toast.error('Error occurred while sending the email.');
        Logger.error(err);
      });
  }

  saveMailAsDraft(f: NgForm): void {
    this.results.toAddresses = this.tos.join(';');
    this.results.cc = this.ccs.join(';');
    this.results.governanceRequestId = this.governanceRequestId;
    this.results.governanceRequestActivityId = this.activity.id;

    this.loader.show();
    this.documentUploadService.governanceSaveAsDraft(this.results)
      .pipe(finalize(() => this.loader.clear()))
      .subscribe(resp => {
        this.results.emailHistoryId = resp ? resp.data : this.results.emailHistoryId;
        this.toast.success('Mail saved as draft!');
      }, () => this.toast.error('Unable to save the draft.'));
  }
}
